import { Button, Input, Modal, Select, Table, Space, DatePicker } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { SavedSearches, orderColumns } from './constants';
import ReportEditor from '../ReportEditor';
import "./orders.css";
import FloatLabel from '../../components/FloatingLabel';
import axiosInstance, { BASE_API } from '../../axios';
import { getUserDetails, hisStatusOptions, makePostCall } from '../../utils/helper';
import { useSites } from '../../hooks/useSites';
import { useModalities } from '../../hooks/useModalities';
import { useBodyParts } from '../../hooks/useBodyParts';
import dayjs from 'dayjs';
import FyzksInput from '../../components/FyzksInput';
import { debounce } from 'lodash';
import { FieldLabels } from '../../constants/fieldLabels';

const { RangePicker } = DatePicker;

const OrdersList = ({ StyledTabsComponent }) => {
  const [orders, setOrders] = useState({ data: [], loading: true, total: 0 });
  const [reportEditorModal, setReportEditorModal] = useState({ visible: false, data: {} });
  const [saveFiltersModal, setSaveFiltersModal] = useState({ visible: false, data: {} });
  const [filterName, setFilterName] = useState(null);
  const [filters, setFilters] = useState({});
  const [savedFilters, setSavedFilters] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const userDetails = getUserDetails();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  // Use dynamic configuration hooks
  const { siteOptions, loading: sitesLoading } = useSites();
  const { modalityOptions, loading: modalitiesLoading } = useModalities();
  const { bodyPartOptions, loading: bodyPartsLoading } = useBodyParts();
  const today = dayjs();
  const yesterday = dayjs().subtract(1, 'days');

  const userType = userDetails?.user_type;

  // Set the default value to [yesterday, today]
  const [dateRange, setDateRange] = useState([yesterday, today]);

  const isHOD = userDetails?.user_type === 'hod';

  useEffect(() => {
    // getOrdersList();
    getSavedFilters();
    getQuickFilters();
  }, []);

  useEffect(() => {
    console.log("Filters changed", filters);
    // Reset to page 1 when filters change
    if (Object.keys(filters).length > 0 || dateRange) {
      setPagination({ current: 1, pageSize: pagination.pageSize });
    }
    debouncedFilter(filters);
  }, [filters]);

  useEffect(() => {
    console.log("date changed", filters);
    // if (Object.keys(filters).length) {
    debouncedFilter(filters);
    // }
  }, [dateRange]);

  // const debouncedFilter = debounce(() => { filterResults() }, 300);

  const debouncedFilter = useCallback(
    debounce((tempFilters) => {
      console.log('Debounced value:');
      // When filters change via debounce, use page 1 (already set in useEffect)
      filterResults(tempFilters, 1, pagination.pageSize);
    }, 500),
    [dateRange, pagination.pageSize]
  );

  const onSave = (newContent, status, currentReport, moreInfo, callback) => {
    const { proxy_user, co_signing_doctor } = moreInfo;
    makePostCall('/submit-report', {
      html: newContent,
      yh_no: reportEditorModal.data?.po_pin,
      order_no: reportEditorModal.data?.po_ord_no,
      order_id: reportEditorModal?.data?.pacs_order?.pacs_ord_id,
      acc_no: reportEditorModal.data?.po_acc_no,
      user_id: getUserDetails()?.username,
      ...moreInfo,
      proxy_user: proxy_user,
      co_signing_doctor: co_signing_doctor,
      status,
      report_id: currentReport?.pr_id,
    })
      .then(res => {
        // console.log("resp", res);
        callback && callback();
      })
      .catch(e => {
        console.log(e);
      });
  }


  const getOrdersList = async () => {
    makePostCall('/orders', {
      role: userDetails?.user_type,
    })
      .then(res => {
        // console.log("resp", res);
        setOrders({ data: res.data?.data || [], loading: false })
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false })
      });
  }

  const cancelReport = () => {
    // console.log("cancel report");
    setReportEditorModal({ visible: false, data: {} });
  }

  const openReport = (record) => {
    return;
    setReportEditorModal({ visible: true, data: record })
  }

  const getSavedFilters = async () => {
    makePostCall('/get-saved-filters', { user_id: getUserDetails()?.username })
      .then(res => {
        // console.log("resp", res);
        setSavedFilters(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setSavedFilters([]);
      });
  }

  const getQuickFilters = async () => {
    makePostCall('/get-quick-filters')
      .then(res => {
        setQuickFilters(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setQuickFilters([]);
      });
  }

  const handleQuickFilters = (filter) => {
    // Apply the quick filter based on qf_key and qf_value
    setFilters(prevFilters => ({
      ...prevFilters,
      [filter.qf_key]: filter.qf_value
    }));
  }

  const submitSaveFilters = () => {
    const payload = {
      filters: JSON.stringify(filters),
      uf_filter_name: filterName,
      user_id: getUserDetails()?.username
    };
    // console.log("Filters", payload);
    makePostCall('/save-my-filters', payload)
      .then(res => {
        // console.log("resp", res);
        getSavedFilters();
      })
      .catch(e => {
        console.log(e);
      });
  }

  const clearFilters = () => {
    setFilters({});
    setDateRange(null);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    filterResults({}, 1, pagination.pageSize);
  }

  const filterResults = (tempFilters, page = 1, pageSize = 20) => {
    console.log("filterResults", tempFilters);

    setOrders({ ...orders, loading: true, data: [] });
    const payload = {
      ...tempFilters,
      page: page - 1, // Backend expects 0-indexed page
      limit: pageSize
    };

    if (dateRange) {
      payload['from_date'] = dayjs(dateRange[0]).format('YYYYMMDD');
      payload['to_date'] = dayjs(dateRange[1]).format('YYYYMMDD');
      payload['po_study_date'] = dateRange;
    }

    console.log("payload", payload);

    makePostCall('/orders', payload)
      .then(res => {
        // console.log("resp", res);
        setOrders({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0
        })
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false, total: 0 })
      });
  }

  const handleFilterChange = (key, value) => {
    // console.log("handleFilterChange", key, value);
    const temp_filters = { ...filters };
    temp_filters[key] = value;
    setFilters(temp_filters);
  }

  const statusOptions = userDetails?.user_type === 'doc' ? [
    { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
  ] :
    [
      { label: 'PENDING', value: 'PENDING' },
      { label: 'SCANNED', value: 'SCANNED' },
      { label: 'DRAFTED', value: 'DRAFTED' },
      { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
    ]

  // All options now come from dynamic hooks:
  // siteOptions, modalityOptions, bodyPartOptions

  const handleFilterSelection = (selectedSavedFilter) => {
    // console.log("selectedSavedFilter", selectedSavedFilter);
    const filterString = selectedSavedFilter.uf_filter_json;
    const filterJson = JSON.parse(filterString);
    // console.log("filter json", filterJson);

    setFilters(filterJson);
  }

  return (
    <div>
      {/* Tab bar with action buttons */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>{StyledTabsComponent && StyledTabsComponent()}</div>
        <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.25rem' }}>
          <button
            className="btn-secondary"
            onClick={() => {
              clearFilters();
            }}
          >
            Clear Filters
          </button>
          <button
            className="btn-primary"
            onClick={() => {
              setSaveFiltersModal({ visible: true });
            }}
          >
            Save Filters
          </button>
          <button
            className="btn-primary"
            onClick={() => filterResults(filters)}
          >
            Search
          </button>
        </div>
      </div>
      <div style={{ padding: '0 1.25rem' }}>
        <div
          style={{
            background: '#FFFFFF',
            borderRadius: '0.75rem',
            border: '1px solid #E2E8F1',
            padding: '1rem 1.25rem',
            marginBottom: '1rem',
          }}
        >
          <SavedSearches
            savedFilters={savedFilters || []}
            handleFilterSelection={handleFilterSelection}
            quickFilters={quickFilters || []}
            handleQuickFilters={handleQuickFilters}
          />
          <div
            className="!"
            style={{ marginTop: '1rem' }}
          >
            <div className="orders-filters-grid">
              <FloatLabel
                label={FieldLabels.PATIENT_NAME}
                value={filters['pat_name']}
              >
                <FyzksInput
                  value={filters['pat_name'] || ''}
                  width={400}
                  onChange={e => handleFilterChange('pat_name', e.target.value)}
                />
              </FloatLabel>

              <FloatLabel
                label={FieldLabels.PATIENT_ID}
                value={filters['pat_pin']}
              >
                <FyzksInput
                  value={filters['pat_pin'] || ''}
                  width={200}
                  onChange={e => handleFilterChange('pat_pin', e.target.value)}
                />
              </FloatLabel>

              <FloatLabel
                label="Acc. No"
                value={filters['po_acc_no']}
              >
                <FyzksInput
                  value={filters['po_acc_no'] || ''}
                  width={200}
                  onChange={e => handleFilterChange('po_acc_no', e.target.value)}
                />
              </FloatLabel>

              <FloatLabel
                label="Body Part / Study Desc"
                value={filters['po_body_part']}
              >
                <FyzksInput
                  value={filters['po_body_part'] || ''}
                  width={200}
                  onChange={e => handleFilterChange('po_body_part', e.target.value)}
                />
              </FloatLabel>

              <FloatLabel
                label="Request Date"
                value={dateRange}
              >
                <RangePicker
                  style={{ width: 400 }}
                  size="middle"
                  value={dateRange}
                  onChange={val => {
                    if (val) {
                      setDateRange([val[0], val[1]]);
                    } else {
                      setDateRange(null);
                    }
                  }}
                />
              </FloatLabel>

              <FloatLabel
                label="Order No"
                value={filters['his_ord_no']}
              >
                <FyzksInput
                  value={filters['his_ord_no'] || ''}
                  width={200}
                  onChange={e => handleFilterChange('his_ord_no', e.target.value)}
                />
              </FloatLabel>

              <FloatLabel
                label="Status"
                value={filters['pacs_status']}
              >
                <Select
                  value={filters['pacs_status']}
                  allowClear
                  style={{ width: 200 }}
                  options={statusOptions}
                  onChange={val => handleFilterChange('pacs_status', val)}
                />
              </FloatLabel>

              <FloatLabel
                label="Modality"
                value={filters['modality']}
              >
                <Select
                  mode="multiple"
                  allowClear
                  value={filters['modality']}
                  style={{ width: 200 }}
                  options={modalityOptions}
                  onChange={val => handleFilterChange('modality', val)}
                />
              </FloatLabel>
            </div>
          </div>
        </div>
        <div className="orders-list">
          <Table
            tableLayout="fixed"
            loading={orders.loading}
            columns={orderColumns(openReport)}
            dataSource={orders.data || []}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: orders?.total || 0,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination({
                  current: page,
                  pageSize,
                });
                // Trigger API call with new page
                filterResults(filters, page, pageSize);
              },
              showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
              position: ['bottomCenter'],
            }}
            onRow={(record, rowIndex) => {
              return {};
            }}
            style={{ width: '100%' }}
            scroll={{
              x: 1200,
              y: 'calc(100vh - 27rem)',
            }}
          />

          {saveFiltersModal.visible && (
            <Modal
              style={{
                height: 'auto',
              }}
              styles={{
                wrapper: {
                  transformOrigin: 'top center',
                },
              }}
              className="save-filter-modal"
              onCancel={() => {
                setSaveFiltersModal({ visible: false });
              }}
              okButtonProps={{ disabled: !filterName }}
              onOk={submitSaveFilters}
              open={saveFiltersModal.visible}
            >
              <div className="pt-4">
                <label>Enter a name for this filter set:</label>
                <Input
                  placeholder="Type the name for the filters"
                  width={300}
                  onChange={e => setFilterName(e.target.value)}
                />
              </div>
            </Modal>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrdersList;
